import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Formata uma data no formato brasileiro (dd/MM/yyyy) ou ISO (yyyy-MM-dd).
 * Ignora informações de hora/minuto se presentes.
 *
 * @param date - String ou objeto Date
 * @param formatType - 'br' para dd/MM/yyyy (padrão), 'iso' para yyyy-MM-dd
 * @returns Data formatada como string, ou string vazia se inválida
 */
export function formatDate(date: string | Date, formatType: 'br' | 'iso' = 'br'): string {
  if (!date) return '';

  try {
    let dateObj: Date;

    if (typeof date === 'string') {
      const [datePart] = date.trim().split(/[\sT]/); // remove qualquer hora
      let year: number, month: number, day: number;

      if (datePart.includes('-')) {
        [year, month, day] = datePart.split('-').map(Number);
      } else if (datePart.includes('/')) {
        [day, month, year] = datePart.split('/').map(Number);
      } else {
        return '';
      }

      if ([year, month, day].some(isNaN)) return '';
      dateObj = new Date(year, month - 1, day);
    } else {
      dateObj = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    return formatType === 'iso' ? format(dateObj, 'yyyy-MM-dd') : format(dateObj, 'dd/MM/yyyy');
  } catch {
    return '';
  }
}

/**
 * Formata uma data e hora no formato brasileiro (dd/MM/yyyy HH:mm) ou ISO (yyyy-MM-dd HH:mm).
 * Se hora não for informada, assume 00:00.
 *
 * @param dateTime - String ou objeto Date
 * @param formatType - 'br' para dd/MM/yyyy HH:mm (padrão), 'iso' para yyyy-MM-dd HH:mm
 * @returns Data e hora formatadas como string, ou string vazia se inválida
 */
export function formatDateTime(dateTime: string | Date, formatType: 'br' | 'iso' = 'br'): string {
  if (!dateTime) return '';

  try {
    let dateObj: Date;

    if (typeof dateTime === 'string') {
      const [datePart, timePart = '00:00'] = dateTime.trim().split(/[\sT]/);
      let year: number, month: number, day: number;

      if (datePart.includes('-')) {
        [year, month, day] = datePart.split('-').map(Number);
      } else if (datePart.includes('/')) {
        [day, month, year] = datePart.split('/').map(Number);
      } else {
        return '';
      }

      if ([year, month, day].some(isNaN)) return '';

      const [hour = 0, minute = 0] = timePart.split(':').map(Number);
      dateObj = new Date(year, month - 1, day, hour, minute);
    } else {
      dateObj = dateTime;
    }

    return formatType === 'iso'
      ? format(dateObj, 'yyyy-MM-dd HH:mm')
      : format(dateObj, 'dd/MM/yyyy HH:mm');
  } catch {
    return '';
  }
}

export function getCurrentDateTime(): string {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm");
}

/*
export function formatDateDisplay(dateString: string): string {
  return formatDate(dateString);
}

export function formatDateForInput(date: string | Date): string {
  if (!date) return '';
  try {
    return format(new Date(date), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
}

export function formatRelativeTime(date: string | Date): string {
  if (!date) return '';
  try {
    return formatDistanceToNow(new Date(date), {
      addSuffix: true,
      locale: ptBR,
    });
  } catch {
    return '';
  }
}

export function getCurrentDateTime(): string {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm");
}

*/

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function formatDocument(doc: string, type: 'CPF' | 'CNPJ'): string {
  const numbers = doc.replace(/\D/g, '');
  if (type === 'CPF') {
    return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

export function formatPhone(phone: string): string {
  const numbers = phone.replace(/\D/g, '');
  if (numbers.length === 11) {
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
}

export function formatCEP(cep: string): string {
  const numbers = cep.replace(/\D/g, '');
  return numbers.replace(/^(\d{5})(\d{3})$/, '$1-$2');
}

export function unformatDocument(doc: string): string {
  return doc.replace(/\D/g, '');
}
